<template>
  <div class="hpf-Filters">
    <div class="hpf-Filters_Select">
      <OkrFilterSelect
        :data-auto-testid="HOMEPAGE_WORKSPACES_FILTER_TEST_ID"
        :data-testid="HOMEPAGE_WORKSPACES_FILTER_TEST_ID"
        :dropdown-min-width="224"
        :model-value="selectedWorkspaceIds"
        :options="workspaces"
        :search-function="searchWorkspaces"
        has-only-this-button
        n-selected-label="workspaces.workspaces"
        prepend-icon="workspace"
        @update:model-value="onUpdateSelectedWorkspaceIds"
      >
        <template #option-label="{ option }">
          <WorkspaceSelectOption
            v-if="option"
            :data-auto-testid="HOMEPAGE_WORKSPACES_FILTER_OPTION_TEST_ID"
            :data-testid="HOMEPAGE_WORKSPACES_FILTER_OPTION_TEST_ID"
            :workspace="option"
            hide-lock-icon
            show-tooltip
          />
        </template>
      </OkrFilterSelect>
    </div>

    <div class="hpf-Filters_Interval">
      <IntervalSelectWithCustomRange
        v-model:interval-custom-date="intervalCustomDate"
        v-model:interval-custom-range="intervalCustomRange"
        v-model:selected-dates="selectedDates"
        v-model:selected-interval-custom-range-mode="selectedIntervalCustomRangeMode"
        v-model:selected-intervals="intervalTypeId"
        v-model:show-interval-custom-range-picker="showIntervalCustomRangePicker"
        :custom-range-options-based-on-filters="customRangeOptionsBasedOnFilters"
        :data-auto-testid="HOMEPAGE_INTERVAL_FILTER_TEST_ID"
        :data-testid="HOMEPAGE_INTERVAL_FILTER_TEST_ID"
        :dropdown-min-width="280"
        :interval-fixed-items="intervalFixedItems"
        :intervals="intervalFilterOptions"
        :is-between-mode-selected="isBetweenModeSelected"
        :is-time-range-selected="isTimeRangeSelected"
        :multi="false"
        @select-intervals="onSelectIntervals"
        @on-bottom-fixed-item-click="onBottomFixedItemClick"
      >
        <template v-if="!isTimeRangeSelected" #button-content-counter>
          <span
            v-tippy="{
              content: t('homepage.interval_filter.counter_tooltip', {
                count: selectedIntervalIdsCount
              }),
              placement: 'top'
            }"
            :data-auto-testid="HOMEPAGE_INTERVAL_FILTER_COUNTER_TEST_ID"
            :data-testid="HOMEPAGE_INTERVAL_FILTER_COUNTER_TEST_ID"
          >
            {{ selectedIntervalIdsCount }}
          </span>
        </template>
        <template #option-label="{ option, selected }">
          <div
            v-if="option"
            :class="{ 'hpf-IntervalFilterOption-selected': selected }"
            :data-auto-testid="HOMEPAGE_INTERVAL_FILTER_OPTION_TEST_ID"
            :data-testid="HOMEPAGE_INTERVAL_FILTER_OPTION_TEST_ID"
            class="hpf-IntervalFilterOption"
          >
            <div class="hpf-IntervalFilterOption_Text">
              <div class="hpf-IntervalFilterOption_Title oboard-truncated-text">
                {{ option.label }}
              </div>
              <div
                v-if="option.subtitle"
                class="hpf-IntervalFilterOption_Subtitle oboard-truncated-text"
              >
                {{ option.subtitle }}
              </div>
            </div>
            <span class="hpf-IntervalFilterOption_Radio" />
          </div>
        </template>
        <template #custom-range-picker-button="{ item, displayedLockedDatesOption }">
          <div
            :class="{ 'hpf-IntervalFilterOption-selected': isTimeRangeSelected }"
            :data-auto-testid="HOMEPAGE_CUSTOM_RANGE_PICKER_TRIGGER_TEST_ID"
            :data-testid="HOMEPAGE_CUSTOM_RANGE_PICKER_TRIGGER_TEST_ID"
            class="hpf-IntervalFilterOption hpf-IntervalFilterOption_CustomRange oboard-truncated-text"
          >
            <div class="hpf-IntervalFilterOption_Text">
              <div class="hpf-IntervalFilterOption_Title oboard-truncated-text">
                {{ $t(item.text) }}
              </div>
              <div class="hpf-IntervalFilterOption_Subtitle oboard-truncated-text">
                {{ getCustomRangeOptionSubtitle({ displayedLockedDatesOption }) }}
              </div>
            </div>
            <span class="hpf-IntervalFilterOption_Radio" />
          </div>
        </template>
      </IntervalSelectWithCustomRange>
    </div>
  </div>
</template>

<script setup>
import dayjs from 'dayjs'
import { cloneDeep, isEmpty } from 'lodash'
import { computed, ref, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import { useSettingsBackButton } from '@/composables/settings-back-button'
import { ROUTE_NAMES } from '@/routes/route-helpers'
import { getLabelForQuarter, getLabelForYear } from '@/utils/date'
import { WORKSPACE_ENTITY_KEYS } from '@/utils/entity-keys'
import { HOME_PAGE_INTERVAL_FILTER_VALUES } from '@/utils/homepage'
import { homePageStateInjectionKey } from '@/utils/injection-keys'
import { DEFAULT_DATES_RANGE, DEFAULT_INTERVAL_CUSTOM_DATE_OBJECT } from '@/utils/interval'
import { localSearch } from '@/utils/objectives'
import {
  DEFAULT_CUSTOM_RANGE_VALUE,
  INTERVAL_TIME_RANGE_OPTIONS,
  isTimeRangeSelectedInsteadOfInterval,
  NEW_HP_FILTERS_KEYS
} from '@/utils/okr-elements/filters'
import {
  EDIT_WORKSPACE_ACTIVE_TAB_QUERY_KEY,
  EDIT_WORKSPACE_QUERY_KEY
} from '@/utils/query-parameters'
import { TABLE_COLUMNS_KEYS } from '@/utils/table-columns'

import {
  HOMEPAGE_CUSTOM_RANGE_PICKER_TRIGGER_TEST_ID,
  HOMEPAGE_INTERVAL_FILTER_COUNTER_TEST_ID,
  HOMEPAGE_INTERVAL_FILTER_OPTION_TEST_ID,
  HOMEPAGE_INTERVAL_FILTER_TEST_ID,
  HOMEPAGE_WORKSPACES_FILTER_OPTION_TEST_ID,
  HOMEPAGE_WORKSPACES_FILTER_TEST_ID
} from '@/components/home-page/jest-helpers'
import OkrFilterSelect from '@/components/objectives/toolbar/OkrFilterSelect'
import {
  CUSTOM_RANGE_ITEM,
  isSelectedIntervalCustomRangeMode
} from '@/components/ui/IntervalSelectWithCustomRange/interval-select-with-custom-range-utils'
import IntervalSelectWithCustomRange from '@/components/ui/IntervalSelectWithCustomRange/IntervalSelectWithCustomRange'
import WorkspaceSelectOption from '@/components/ui/WorkspaceSelectOption/WorkspaceSelectOption'

defineOptions({
  name: 'HomePageFilters'
})

const { ALL_CURRENT, QUARTER, YEAR, CUSTOM_RANGE } = HOME_PAGE_INTERVAL_FILTER_VALUES
const MANAGE_INTERVALS_ACTION_ID = 1

const store = useStore()
const { t } = useI18n()

const { saveSettingsReferer } = useSettingsBackButton()

const workspaces = computed(() => {
  return store.state.workspaces.workspaces.toSorted(
    (a, b) => a[WORKSPACE_ENTITY_KEYS.ORDER] - b[WORKSPACE_ENTITY_KEYS.ORDER]
  )
})

const homePageState = inject(homePageStateInjectionKey)
const homePageFilters = homePageState.filterValues

//#region Interval filter
const intervalTypeId = computed({
  get() {
    return homePageFilters.value[NEW_HP_FILTERS_KEYS.INTERVAL_TYPE_ID]
  },
  set(value) {
    if (Array.isArray(value) && isEmpty(value)) {
      homePageFilters.value[NEW_HP_FILTERS_KEYS.INTERVAL_TYPE_ID] = CUSTOM_RANGE
    } else {
      homePageFilters.value[NEW_HP_FILTERS_KEYS.INTERVAL_TYPE_ID] = value
      homePageFilters.value[NEW_HP_FILTERS_KEYS.START_DATES] = [...DEFAULT_CUSTOM_RANGE_VALUE]
      homePageFilters.value[NEW_HP_FILTERS_KEYS.DUE_DATES] = [...DEFAULT_CUSTOM_RANGE_VALUE]
    }
  }
})
const selectedDates = computed({
  get() {
    return {
      startDates: homePageFilters.value[NEW_HP_FILTERS_KEYS.START_DATES],
      dueDates: homePageFilters.value[NEW_HP_FILTERS_KEYS.DUE_DATES]
    }
  },
  set(value) {
    homePageFilters.value[NEW_HP_FILTERS_KEYS.START_DATES] = [...value.startDates]
    homePageFilters.value[NEW_HP_FILTERS_KEYS.DUE_DATES] = [...value.dueDates]
  }
})
const selectedWorkspaceIds = computed(
  () => homePageFilters.value[NEW_HP_FILTERS_KEYS.WORKSPACE_IDS]
)
const onSelectIntervals = value => {
  intervalTypeId.value = value
}

const selectedIntervalIds = homePageState.selectedIntervalIds
const selectedIntervalIdsCount = computed(() => {
  return selectedIntervalIds.value.length
})

const intervalFilterOptions = computed(() => [
  {
    label: t('homepage.interval_filter.all_current'),
    id: ALL_CURRENT
  },
  {
    label: t('homepage.interval_filter.current_quarters'),
    id: QUARTER,
    subtitle: getLabelForQuarter(dayjs())
  },
  {
    label: t('homepage.interval_filter.current_years'),
    id: YEAR,
    subtitle: getLabelForYear(dayjs())
  }
])

const showIntervalCustomRangePicker = ref(false)
const selectedIntervalCustomRangeMode = ref(INTERVAL_TIME_RANGE_OPTIONS.BETWEEN.value)
const intervalCustomRange = ref(cloneDeep(DEFAULT_DATES_RANGE))
const intervalCustomDate = ref(cloneDeep(DEFAULT_INTERVAL_CUSTOM_DATE_OBJECT))

const customRangeOptionsBasedOnFilters = computed(() => {
  const { startDates, dueDates } = selectedDates.value || {}

  return isTimeRangeSelectedInsteadOfInterval({
    startDates,
    dueDates,
    intervals: selectedIntervalIds.value
  })
})

const isTimeRangeSelected = computed(() => {
  return customRangeOptionsBasedOnFilters.value.isTimeRangeSelected
})

const getCustomRangeOptionSubtitle = ({ displayedLockedDatesOption }) => {
  if (isTimeRangeSelected.value && displayedLockedDatesOption?.full) {
    return displayedLockedDatesOption.full
  }

  return t('homepage.interval_filter.custom_range_hint')
}

const isBetweenModeSelected = computed(
  () =>
    isSelectedIntervalCustomRangeMode(selectedIntervalCustomRangeMode.value).isBetweenModeSelected
)

const manageIntervalsItem = computed(() => {
  const normalizedSelectedWorkspaceIds = new Set(selectedWorkspaceIds.value.map(Number))

  const workspacesWithAdminAccess = workspaces.value.filter(workspace => {
    return (
      normalizedSelectedWorkspaceIds.has(Number(workspace[WORKSPACE_ENTITY_KEYS.ID])) &&
      workspace.workspaceAdmin
    )
  })

  if (isEmpty(workspacesWithAdminAccess)) {
    return null
  }

  // without query the link opens the workspaces list, that is the case of several admin workspaces
  const path = { name: ROUTE_NAMES.SETTINGS_WORKSPACES }

  if (workspacesWithAdminAccess.length === 1) {
    const [firstWorkspace] = workspacesWithAdminAccess

    path.query = {
      [EDIT_WORKSPACE_QUERY_KEY]: firstWorkspace[WORKSPACE_ENTITY_KEYS.ID],
      [EDIT_WORKSPACE_ACTIVE_TAB_QUERY_KEY]: TABLE_COLUMNS_KEYS.INTERVALS
    }
  }

  return {
    id: MANAGE_INTERVALS_ACTION_ID,
    path,
    text: 'filter.manage_intervals'
  }
})

const intervalFixedItems = computed(() => {
  const items = [
    {
      ...CUSTOM_RANGE_ITEM,
      value: CUSTOM_RANGE
    }
  ]

  if (manageIntervalsItem.value) {
    items.push(manageIntervalsItem.value)
  }

  return items
})

const onBottomFixedItemClick = ({ handler, item }) => {
  if (handler && item?.id === MANAGE_INTERVALS_ACTION_ID) {
    saveSettingsReferer()
  }

  if (handler) {
    handler()
  }
}

//#endregion

const searchWorkspaces = (searchString = '') => {
  return localSearch({ value: searchString || '', options: workspaces.value })
}

const onUpdateSelectedWorkspaceIds = value => {
  if (isEmpty(value)) {
    homePageFilters.value[NEW_HP_FILTERS_KEYS.WORKSPACE_IDS] = [
      ...homePageFilters.value[NEW_HP_FILTERS_KEYS.WORKSPACE_IDS]
    ]
    return
  }
  homePageFilters.value[NEW_HP_FILTERS_KEYS.WORKSPACE_IDS] = value
}
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/mixins';
@import '~@/assets/styles/selected-items-counter';

.hpf-Filters {
  display: flex;
  gap: 8px;
  overflow: hidden;
}

.hpf-Filters_Select {
  max-width: 260px;
}

.hpf-Filters_Interval {
  max-width: 260px;
}

.hpf-IntervalFilterOption {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
  padding-right: 2px;
}

.hpf-IntervalFilterOption_Text {
  min-width: 0;
}

.hpf-IntervalFilterOption_Title {
  font-weight: fw('bold');
  font-size: $fs-14;
  line-height: 20px;
  color: $dark-1;
}

.hpf-IntervalFilterOption_Subtitle {
  font-size: $fs-12;
  line-height: 16px;
  color: $dark-3;
}

.hpf-IntervalFilterOption_Radio {
  flex-shrink: 0;
  box-sizing: border-box;
  width: 16px;
  height: 16px;
  border: 2px solid $dark-3;
  border-radius: 50%;
}

.hpf-IntervalFilterOption-selected {
  .hpf-IntervalFilterOption_Title {
    color: $primary-color-next;
  }

  .hpf-IntervalFilterOption_Radio {
    border-color: $primary-color-next;
    border-width: 5px;
    background-color: $white;
  }
}

.hpf-IntervalFilterOption_CustomRange {
  box-sizing: border-box;
  width: 100%;
  min-height: 44px;
  padding: 10px 12px;
  cursor: pointer;

  &:not(.hpf-IntervalFilterOption-selected) {
    @media (any-hover: hover) {
      &:hover {
        background-color: $grey-19;
      }
    }

    @include activeState($grey-19);
  }

  &.hpf-IntervalFilterOption-selected {
    background-color: rgba($primary-color-next, 0.15);
  }
}
</style>
